/**
 * FenzSafe jurisdiction/legal-resource integration seam -- the frontend half (Issue #164 founder
 * continuation directive, item 2). Talks only to the read-only endpoints
 * app/FenzSafe/Http/JurisdictionGuidanceController.php exposes; carries no rules logic of its own.
 */
export type JurisdictionClassifications = {
  configured: boolean;
  classifications: {
    jurisdiction_id: string[];
    matter_type: string[];
    target_type: string[];
    service_method: string[];
  };
  serviceOutcomes: string[];
};

export type JurisdictionGuidanceRequest = {
  jurisdictionId: string;
  matterType: string;
  targetType: string;
  serviceMethod: string;
  serviceOutcome: string;
  issuingCourtId: string;
  divisionId: string;
};

export type JurisdictionCitation = {
  rule_id: string | null;
  governing_citation: string | null;
  primary_source_citation: string | null;
  research_sources: Array<Record<string, string>>;
};

export type JurisdictionGuidanceResult = {
  status: string;
  confirmed: boolean;
  mustEscalate: boolean;
  citations: JurisdictionCitation[];
  reviewReasons: string[];
};

async function request(path: string, method: string, body?: Record<string, unknown>): Promise<Record<string, unknown>> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    throw new Error('jurisdiction_guidance_unavailable');
  }
  return (await response.json()) as Record<string, unknown>;
}

export async function fetchJurisdictionClassifications(): Promise<JurisdictionClassifications> {
  const body = await request('/api/v1/fenzsafe/jurisdiction-guidance/classifications', 'GET');
  const classifications = body.classifications as JurisdictionClassifications['classifications'];

  return {
    configured: body.configured === true,
    classifications,
    serviceOutcomes: (body.service_outcomes as string[] | undefined) ?? [],
  };
}

export async function fetchJurisdictionGuidance(input: JurisdictionGuidanceRequest): Promise<JurisdictionGuidanceResult> {
  const body = await request('/api/v1/fenzsafe/jurisdiction-guidance', 'POST', {
    country_code: 'US',
    jurisdiction_id: input.jurisdictionId,
    matter_type: input.matterType,
    target_type: input.targetType,
    service_method: input.serviceMethod,
    service_outcome: input.serviceOutcome,
    issuing_court_id: input.issuingCourtId,
    division_id: input.divisionId,
  });
  const guidance = body.guidance as Record<string, unknown>;

  return {
    status: String(guidance.status ?? ''),
    confirmed: guidance.confirmed === true,
    mustEscalate: guidance.must_escalate === true,
    citations: (guidance.citations as JurisdictionCitation[] | undefined) ?? [],
    reviewReasons: (guidance.review_reasons as string[] | undefined) ?? [],
  };
}
